import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button, ErrorState, Skeleton } from '@/design/components';

/**
 * מצגת בתוך השיעור (PRD 5.6.1).
 *
 * המצגת היא PDF שההנהלה ייצאה מפאוורפוינט, ו pdf.js מצייר כל עמוד
 * לקנבס. למה לא iframe עם מציג ה PDF של הדפדפן: בספארי בטלפון הוא
 * לא נפתח בתוך הדף, ובאנדרואיד הוא מציג גלילה רציפה ולא שקופיות.
 * כאן העובד מקבל שקופית אחת על המסך, כמו שמצגת אמורה להיראות.
 *
 * הספרייה נטענת רק כשנכנסים לשיעור כזה, ולכן היא לא יושבת בחבילה
 * הראשית של האפליקציה.
 */
export function SlidesViewer({ url, title }: { url: string; title: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docRef = useRef<PdfDocument | null>(null);
  const renderRef = useRef<{ cancel: () => void } | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // טעינת המסמך. ה url חתום ומתחדש מדי פעם, ולכן הוא התלות.
  //
  // אין כאן איפוס של העמוד: כשהכתובת מתחדשת באמצע הצפייה העובד אמור
  // להישאר על אותה שקופית. מעבר לשיעור אחר מרנדר רכיב חדש (key לפי
  // מזהה השיעור), ושם המצב מתחיל מחדש ממילא.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        // ה worker נארז על ידי Vite, ולכן אין כאן כתובת CDN
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.mjs',
          import.meta.url,
        ).toString();

        const doc = (await pdfjs.getDocument({ url }).promise) as unknown as PdfDocument;
        if (cancelled) {
          void doc.destroy();
          return;
        }
        docRef.current = doc;
        setPageCount(doc.numPages);
        // עמוד שנשאר ממסמך קודם ארוך יותר
        setPage((current) => Math.min(current, doc.numPages));
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      renderRef.current?.cancel();
      void docRef.current?.destroy();
      docRef.current = null;
    };
  }, [url]);

  // ציור העמוד הנוכחי
  useEffect(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (status !== 'ready' || !doc || !canvas) return;

    let cancelled = false;

    void (async () => {
      const pdfPage = await doc.getPage(page);
      if (cancelled) return;

      // רוחב הקנבס בפיקסלים אמיתיים, כדי שטקסט בשקופית לא ייצא מטושטש
      const cssWidth = canvas.parentElement?.clientWidth ?? 640;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const base = pdfPage.getViewport({ scale: 1 });
      const viewport = pdfPage.getViewport({ scale: (cssWidth / base.width) * ratio });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = '100%';
      canvas.style.height = 'auto';

      const context = canvas.getContext('2d');
      if (!context) return;

      // ציור קודם שעוד רץ חייב להתבטל, אחרת pdf.js זורק על אותו קנבס
      renderRef.current?.cancel();
      const task = pdfPage.render({ canvas, canvasContext: context, viewport });
      renderRef.current = task;
      try {
        await task.promise;
      } catch {
        // ביטול הוא מצב תקין, לא שגיאה להציג
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, status]);

  if (status === 'error') {
    return (
      <div className="grid gap-2">
        <ErrorState description="לא הצלחנו להציג את המצגת כאן." />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="justify-self-center text-body-sm text-ink"
        >
          לפתיחת המצגת בלשונית חדשה
        </a>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <div className="overflow-hidden rounded-md border border-line bg-raised">
        {status === 'loading' && <Skeleton className="aspect-video w-full" />}
        <canvas
          ref={canvasRef}
          aria-label={`${title}, שקופית ${page} מתוך ${pageCount}`}
          className={status === 'ready' ? 'block w-full' : 'hidden'}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* RTL: "הבאה" שמאלה, "הקודמת" ימינה */}
        <Button
          variant="secondary"
          disabled={page >= pageCount}
          onClick={() => setPage(page + 1)}
        >
          השקופית הבאה
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" />
        </Button>
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          <ChevronRight size={20} strokeWidth={1.75} aria-hidden="true" />
          הקודמת
        </Button>

        <span className="text-body-sm tabular text-muted">
          {pageCount > 0 ? `${page} מתוך ${pageCount}` : ''}
        </span>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="ms-auto inline-flex items-center gap-1 text-body-sm text-muted"
        >
          <Download size={16} strokeWidth={1.75} aria-hidden="true" />
          פתיחה במסך מלא
        </a>
      </div>
    </div>
  );
}

/**
 * רק מה שנדרש כאן מתוך ה API של pdf.js. הספרייה נטענת דינמית, ואת
 * הטיפוסים שלה אי אפשר לייבא בלי לגרור אותה לחבילה הראשית.
 */
type PdfViewport = { width: number; height: number };

type PdfPage = {
  getViewport: (options: { scale: number }) => PdfViewport;
  render: (options: {
    canvas: HTMLCanvasElement;
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewport;
  }) => { promise: Promise<void>; cancel: () => void };
};

type PdfDocument = {
  numPages: number;
  getPage: (page: number) => Promise<PdfPage>;
  destroy: () => Promise<void>;
};
